import { CapManageControllerContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/CapManageEntityFlow.tsx';
import {
	HoldingsType,
	ViewMode,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/useHoldingsController';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { downloadFileFromBlob } from '@dealstack/frontend/shared/files/downloadFile';
import { buildExportFileName } from '@dealstack/frontend/shared/files/exportFileName.utils';
import { formatAsOfFilterDate } from '@dealstack/frontend/shared/utils/utc.ts';
import { useContext, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import {
	getRegularViewHoldingsCSV,
	getDownloadExcel,
	getWideViewHoldingsCSV,
} from '@dealstack/frontend/app/services/Holdings.service.ts';
import { useShareRegisterPdfGeneration } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/useShareRegisterPdfGeneration';
import { CapTableRail } from '@dealstack/shared/lib/generated';

// CAP-1973: `rail` is the title rail the table is currently showing, so an export matches what was
// on screen. The backend drops it on a deal without a nominee structure, which is also the only
// case where the tabs leave it undefined.
export const useHoldingsBulkAction = (
	viewMode: ViewMode,
	tableType: HoldingsType,
	filterDate: Date | null,
	rail?: CapTableRail
) => {
	const tCapManage = useTranslationCapManage();
	const { dealId, selectedEntityId, selectedEntityName } = useContext(CapManageControllerContext);
	const [isLoading, setIsLoading] = useState(false);
	const isOwnership = tableType !== HoldingsType.HoldingsTable;
	const isBlockView = viewMode === ViewMode.Block;
	const filterDateToString = formatAsOfFilterDate(filterDate) || '';
	const exportDate = filterDate ?? new Date();
	const exportType = isOwnership ? 'Ownership' : 'Holdings';

	const getWideTableCSVRequest = useMutation({
		mutationKey: [
			API_PATHS.CAP_WIDE_VIEW_CSV_POST,
			{
				body: {
					personId: selectedEntityId,
					dealId,
					isOwnership,
					rail,
				},
			},
		],

		mutationFn: getWideViewHoldingsCSV,
	});

	const getRegularTableCSVRequest = useMutation({
		mutationKey: [
			API_PATHS.CAP_REGULAR_VIEW_CSV_POST,
			{
				body: {
					personId: selectedEntityId,
					dealId,
					isOwnership,
					viewByBlock: isBlockView,
					rail,
				},
			},
		],

		mutationFn: getRegularViewHoldingsCSV,
	});

	const getCSVRequest =
		viewMode === ViewMode.Wide ? getWideTableCSVRequest : getRegularTableCSVRequest;

	const downloadCSV = async () => {
		setIsLoading(true);

		try {
			const response = await getCSVRequest.mutateAsync({
				personId: selectedEntityId,
				isOwnership,
				viewByBlock: isBlockView,
				...(filterDate ? { filterDate: formatAsOfFilterDate(filterDate) } : {}),
				...(rail ? { rail } : {}),
			});
			downloadFileFromBlob(
				new Blob([response || ''], { type: 'text/csv' }),
				buildExportFileName(exportType, selectedEntityName, exportDate, 'csv')
			);
		} catch {
			showToastV2({
				state: 'error',
				message: tCapManage('toasts.errors.failed_download_csv'),
			});
		}
		setIsLoading(false);
	};

	const downloadExcel = async () => {
		setIsLoading(true);

		try {
			const blob = await getDownloadExcel({
				personId: selectedEntityId,
				isOwnership,
				filterDate: filterDateToString,
				rail,
			});

			const url = window.URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = buildExportFileName(exportType, selectedEntityName, exportDate, 'xlsx');
			document.body.appendChild(a);
			a.click();
			setTimeout(() => {
				window.URL.revokeObjectURL(url);
				a.remove();
			}, 100);
		} catch {
			showToastV2({
				state: 'error',
				message: tCapManage('toasts.errors.failed_download_csv'),
			});
		}

		setIsLoading(false);
	};

	const [shareRegisterDrawerOpen, setShareRegisterDrawerOpen] = useState(false);

	const shareRegisterFileName = buildExportFileName(
		'Share Register',
		selectedEntityName,
		exportDate,
		'pdf'
	);

	const shareRegister = useShareRegisterPdfGeneration({
		fileName: shareRegisterFileName,
		onError: () =>
			showToastV2({
				state: 'error',
				message: tCapManage('toasts.errors.failed_download_share_register'),
			}),
	});

	const downloadShareRegister = () => {
		if (!selectedEntityId) {
			return;
		}
		shareRegister.reset();
		shareRegister.start({
			issuerId: selectedEntityId,
			date: filterDateToString || undefined,
		});
		setShareRegisterDrawerOpen(true);
	};

	const closeShareRegisterDrawer = () => {
		// Closing (X / overlay / Escape) only detaches the UI — the Temporal job
		// keeps rendering server-side (there is no cancel endpoint). Tell the user
		// so the resume-on-reopen (in-flight dedupe) isn't a surprise.
		if (shareRegister.isPolling) {
			showToastV2({
				state: 'warning',
				title: tCapManage('toasts.share_register_background.title'),
				message: tCapManage('toasts.share_register_background.message'),
			});
		}
		setShareRegisterDrawerOpen(false);
		shareRegister.reset();
	};

	const shareRegisterDrawer = {
		opened: shareRegisterDrawerOpen,
		onClose: closeShareRegisterDrawer,
		status: shareRegister.status?.status,
		progressPercentage: (shareRegister.status?.progress ?? 0) * 100,
		downloadUrl: shareRegister.status?.downloadUrl,
		fileName: shareRegisterFileName,
	};

	return {
		downloadCSV,
		downloadExcel,
		downloadShareRegister,
		shareRegisterDrawer,
		isLoading,
	};
};
